import { CalendarDays, Check, Clock, Info, Phone } from 'lucide-react';
import { formatEuro } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { ImageSlot } from '../../../ds/components';
import type { PortalOverviewBefore } from '../types';
import { PortalAction, countWord } from './actions';
import styles from './Overview.module.css';

type Volume = NonNullable<PortalOverviewBefore['volume']>;

/** Portal 14: the navy volume card with rate, equity and the number of apartments in range. */
export function VolumeCard({ volume }: { volume: Volume }) {
  return (
    <section className={styles.volume} aria-label="Ihr Finanzierungsvolumen">
      <div className={clsx(styles.eyebrow, styles.volumeEyebrow)}>Ihr Finanzierungsvolumen</div>
      <div className={styles.volumeRow}>
        <span className={styles.volumeAmount}>{formatEuro(volume.cents)}</span>
        <span className={styles.volumeAsOf}>{volume.asOfLabel}</span>
      </div>
      <p className={styles.volumeText}>{volume.text}</p>
      <div className={styles.figures}>
        <div>
          <div className={styles.figureLabel}>Monatliche Rate</div>
          <div className={styles.figureValue}>{formatEuro(volume.rateCents)}</div>
        </div>
        {volume.equityCents !== null ? (
          <div>
            <div className={styles.figureLabel}>Eigenkapital</div>
            <div className={styles.figureValue}>{formatEuro(volume.equityCents)}</div>
          </div>
        ) : null}
        <div>
          <div className={styles.figureLabel}>Wohnungen in Ihrem Rahmen</div>
          <div className={clsx(styles.figureValue, styles.figureAccent)}>{volume.unitsInRange}</div>
        </div>
      </div>
    </section>
  );
}

export interface AdvisorLine {
  name: string;
  initials: string;
  phone: string | null;
  organizationName: string;
}

/** Portal 14 „Ihr nächster Schritt" with the advisor row „Ihr Betreuer · <Organisation>". */
export function NextStepCard({
  nextStep,
  requestId,
  advisor,
  preview,
}: {
  nextStep: NonNullable<PortalOverviewBefore['nextStep']>;
  requestId: string | undefined;
  advisor: AdvisorLine | null;
  preview?: boolean | undefined;
}) {
  return (
    <section className={clsx(styles.card, styles.nextStep)} aria-labelledby="portal-next-step">
      <div className={styles.eyebrow}>Ihr nächster Schritt</div>
      <h3 className={styles.nextStepTitle} id="portal-next-step">
        {nextStep.title}
      </h3>
      <p className={styles.nextStepText}>{nextStep.text}</p>
      <PortalAction
        target={{ section: 'documents', requestId }}
        preview={preview}
        className={styles.nextStepCta}
      >
        {nextStep.action.label}
      </PortalAction>
      {advisor ? (
        <div className={styles.advisorRow}>
          <span className={styles.advisorAvatar} aria-hidden="true">
            {advisor.initials}
          </span>
          <div className={styles.advisorText}>
            <div className={styles.advisorName}>{advisor.name}</div>
            <div className={styles.advisorSub}>Ihr Betreuer · {advisor.organizationName}</div>
          </div>
          {advisor.phone && !preview ? (
            <a
              className={styles.iconLink}
              href={`tel:${advisor.phone.replace(/[^\d+]/g, '')}`}
              aria-label={`${advisor.name} anrufen`}
            >
              <Phone size={18} strokeWidth={1.5} aria-hidden="true" />
            </a>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

type Units = NonNullable<PortalOverviewBefore['units']>;

/**
 * The apartments in range (Portal 14/16): real cards (14) or „Wohnung A/B/C" with a masked price (16), blurred and
 * inert under the overlay until the consultation took place.
 */
export function UnitsGate({ units, preview }: { units: Units; preview?: boolean | undefined }) {
  const gated = units.overlay !== null;
  return (
    <section aria-labelledby="portal-units-title">
      <div className={styles.unitsHead}>
        <h3 className={styles.h3} id="portal-units-title">
          {units.title}
        </h3>
        {units.masked ? null : <span className={styles.unitsSub}>{units.subtitle}</span>}
      </div>
      <div className={styles.unitsArea}>
        <div className={clsx(styles.unitGrid, gated && styles.gated)} aria-hidden={gated ? true : undefined}>
          {units.cards.map((card, index) =>
            'label' in card ? (
              <div key={`${card.label}-${index}`} className={clsx(styles.card, styles.unitCard)}>
                <div className={styles.unitMaskImage} />
                <div className={styles.unitBody}>
                  <div className={styles.unitNumber}>{card.label}</div>
                  <div className={styles.unitPrice}>•••.••• €</div>
                </div>
              </div>
            ) : (
              <div key={card.number} className={clsx(styles.card, styles.unitCard)}>
                <ImageSlot
                  alt={`Visualisierung ${card.number}`}
                  placeholder={`Visualisierung ${card.number}`}
                  height={180}
                  radius="none"
                  chrome="frame"
                />
                <div className={styles.unitBody}>
                  <div className={styles.unitTop}>
                    <span className={styles.unitNumber}>{card.number}</span>
                    <span className={clsx(styles.pill, card.pill === 'Vorgemerkt' && styles.pillAccent)}>
                      {card.pill}
                    </span>
                  </div>
                  <div className={styles.unitLine}>{card.line}</div>
                  <div className={styles.unitPrice}>{formatEuro(card.priceCents)}</div>
                </div>
              </div>
            ),
          )}
        </div>
        {units.overlay ? (
          <div className={styles.overlay}>
            {units.overlay.bookCta ? (
              <>
                <div className={styles.overlayText}>{units.overlay.text}</div>
                <PortalAction
                  target={{ section: 'questions', intent: 'appointment' }}
                  preview={preview}
                  className={styles.overlayCta}
                >
                  Beratungstermin buchen
                </PortalAction>
              </>
            ) : (
              <div className={styles.overlayNote}>
                <CalendarDays size={20} strokeWidth={1.5} aria-hidden="true" />
                <span>{units.overlay.text}</span>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Portal 16: the customer's open document requests („Zwei Aufgaben für Sie") with „Hochladen". */
export function TodosCard({
  todos,
  preview,
}: {
  todos: PortalOverviewBefore['todos'];
  preview?: boolean | undefined;
}) {
  const heading = `${countWord(todos.length, true)} ${todos.length === 1 ? 'Aufgabe' : 'Aufgaben'} für Sie`;
  return (
    <section className={styles.todos} aria-labelledby="portal-todos">
      <div className={styles.todosHead}>
        <Clock size={18} strokeWidth={1.75} aria-hidden="true" />
        <h3 className={styles.eyebrow} id="portal-todos">
          {heading}
        </h3>
      </div>
      <ul className={styles.todoList}>
        {todos.map((todo) => (
          <li key={todo.requestId} className={styles.todo}>
            <span className={styles.checkLook} aria-hidden="true" />
            <div className={styles.todoText2}>
              <div className={styles.todoLabel}>{todo.title}</div>
              {todo.dueLabel ? <div className={styles.todoDue}>{todo.dueLabel}</div> : null}
            </div>
            <PortalAction
              target={{ section: 'documents', requestId: todo.requestId }}
              preview={preview}
              className={styles.todoCta}
            >
              Hochladen
            </PortalAction>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Portal 16 „Was danach passiert" with the volume's validity. */
export function AfterCard({ after }: { after: PortalOverviewBefore['after'] }) {
  return (
    <section className={clsx(styles.card, styles.after)} aria-label="Was danach passiert">
      <div className={styles.eyebrow}>Was danach passiert</div>
      <p className={styles.afterText}>{after.text}</p>
      {after.validUntilLabel ? <div className={styles.afterValid}>{after.validUntilLabel}</div> : null}
    </section>
  );
}

/** Portal 17 „Gerade nichts für Sie zu tun" (navy info box) — the advisor's station. */
export function WaitingCard({ waiting }: { waiting: NonNullable<PortalOverviewBefore['waiting']> }) {
  return (
    <section className={styles.waiting} aria-labelledby="portal-waiting">
      <div className={styles.waitingHead}>
        <Info size={18} strokeWidth={1.75} aria-hidden="true" />
        <span className={styles.eyebrow}>Gerade nichts für Sie zu tun</span>
      </div>
      <h3 className={styles.waitingTitle} id="portal-waiting">
        {waiting.title}
      </h3>
      <p className={styles.waitingText}>{waiting.text}</p>
      <div className={styles.waitingFacts}>
        {waiting.sinceLabel ? (
          <div>
            <div className={styles.factLabel}>Seit</div>
            <div className={styles.factValue}>{waiting.sinceLabel}</div>
          </div>
        ) : null}
        <div>
          <div className={styles.factLabel}>Üblicherweise erledigt in</div>
          <div className={styles.factValue}>{waiting.usualLabel}</div>
        </div>
      </div>
    </section>
  );
}

/** Portal 17 „Erledigt": what the customer has already done, with the day. */
export function DoneCard({ done }: { done: PortalOverviewBefore['done'] }) {
  return (
    <section className={clsx(styles.card, styles.done)} aria-label="Erledigt">
      <div className={styles.eyebrow}>Erledigt</div>
      <ul className={styles.doneList}>
        {done.map((item, index) => (
          <li key={`${item.label}-${index}`} className={styles.doneItem}>
            <Check size={16} strokeWidth={1.75} aria-hidden="true" />
            <div>
              <div className={styles.doneLabel}>{item.label}</div>
              <div className={styles.doneOn}>{item.onLabel}</div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
